import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip';
import { cn } from '../../lib/utils';

type InfoHintProps = {
  /** Accessible name for the trigger — says what the hint explains. */
  label: string;
  /** Hint body, rendered inside the tooltip. */
  children: ReactNode;
  /** Fired when a click (not a hover) opens the hint — analytics hook for the host. */
  onOpenByClick?: () => void;
  className?: string;
  'data-testid'?: string;
};

/**
 * Small (i) trigger that reveals a hint on hover **and** on click.
 *
 * Hover alone is unreachable with touch/pen and disappears the moment the
 * pointer moves, which makes a multi-line explanation hard to actually read.
 * A click therefore pins the hint open until it is clicked again, dismissed
 * with Escape, or a click lands elsewhere.
 */
export function InfoHint({
  label,
  children,
  onOpenByClick,
  className,
  'data-testid': testId,
}: InfoHintProps) {
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // A pinned hint outlives the pointer, so it needs its own dismissals.
  useEffect(() => {
    if (!pinned) return;
    const dismissOnOutsideClick = (event: MouseEvent) => {
      if (!triggerRef.current?.contains(event.target as Node)) setPinned(false);
    };
    const dismissOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPinned(false);
    };
    document.addEventListener('mousedown', dismissOnOutsideClick, true);
    document.addEventListener('keydown', dismissOnEscape, true);
    return () => {
      document.removeEventListener('mousedown', dismissOnOutsideClick, true);
      document.removeEventListener('keydown', dismissOnEscape, true);
    };
  }, [pinned]);

  const togglePinned = () => {
    const nextPinned = !pinned;
    setPinned(nextPinned);
    if (nextPinned) onOpenByClick?.();
  };

  return (
    <TooltipProvider>
      {/* `open` is fully controlled so a click can hold the hint open — Radix
          otherwise closes it on pointer-down and reopens it only on hover. */}
      <Tooltip open={pinned || hovered}>
        <TooltipTrigger asChild>
          <button
            ref={triggerRef}
            type="button"
            aria-label={label}
            onClick={togglePinned}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onFocus={() => setHovered(true)}
            onBlur={() => setHovered(false)}
            className={cn(
              'inline-flex items-center justify-center rounded-sm text-muted-foreground',
              'hover:text-foreground focus-visible:outline-none focus-visible:ring-1',
              'focus-visible:ring-ring',
              className,
            )}
            data-testid={testId}
          >
            <Info className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">{children}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
